import { AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './App.css';
import i18n from './i18nextInit';
import { AppStyled, GlobalStyle, IframeResizeTaggedElement } from './styles/basics';
import { CliniqueState, Retailer, StepView } from './type';
import { Footer } from './views/footer';
import LandscapeComponent from './views/landscape';
import { Topper } from './views/topper';
import ViewDispatcher from './views/viewDispatcher';
// import SephoraApp from './SephoraApp'
import { A3CameraContextProvider } from '@imaaagery/a3camera';
import { setIframeResizeTaggedElementHeight } from './constantes/iframeResizer';
import * as Actiontypes from './constantes/actionTypes';
import { MICROSITE_MARKET_LANGUAGES } from './constantes/micrositeMarkets';
import { generateSessionID, initializeMicrositeGtm, parseLanguagesJSON, sendMicrositeCountrySelectedAnalytics } from './constantes/utilitaries';
import ShadematchViewDispatcher from './shadematch/viewDispatcher';
import MicrositeIntro from './views/micrositeIntro';
import DebugLoadingMask from './views/debugLoadingMask';

declare global {
	interface Window {
		RTL_LANGS: string[]
	}
}

function App() {
	const [isLandscape, setLandscape] = useState<boolean>(false);
	const step = useSelector((state: CliniqueState) => state.session.stepView);
	const isShadematch = useSelector((state: CliniqueState) => state.session.isShadematch);
	const isMicrosite = useSelector((state: CliniqueState) => state.session.isMicrosite);
	const searchParams = new URL(window.location.href).searchParams;
	const isMicrositeEntry = searchParams.get('microsite') === 'true';
	const isMicrositeSkinDiagEntry = isMicrositeEntry && searchParams.get('skindiag') === 'true';
	const isMicrositeShadematchEntry = isMicrositeEntry && searchParams.get('shadematch') === 'true';
	const isDebugLoadingMask = searchParams.get('debugMask') === 'true';
	const displayedStep = isMicrositeShadematchEntry && !isShadematch
		? StepView.shadematchPrivacy
		: isMicrositeEntry && !isMicrositeSkinDiagEntry && !isMicrositeShadematchEntry && !isMicrosite
			? StepView.micrositeIntro
			: step;
	const shouldRenderShadematch = isShadematch || isMicrositeShadematchEntry;
	const dispatch = useDispatch();
	const market = useSelector((state: CliniqueState) => state.session.market);
	const imageUser = useSelector((state: CliniqueState) => state.session.imageUser);
	const predictions = useSelector((state: CliniqueState) => state.session.predictions);
	window.RTL_LANGS = ["HeIl", "ArIq"]

	useEffect(() => {
		const updateViewportState = () => {
			setLandscape(window.innerWidth > window.innerHeight);
			document.documentElement.style.setProperty('--vh', `${window.innerHeight / 100}px`);
		};

		updateViewportState();
		window.addEventListener('resize', updateViewportState);

		let url = new URL(window.location.href);
		if (url.searchParams.get('retailer') === 'sephora') {
			dispatch({ type: Actiontypes.UPDATE_RETAILER, value: Retailer.sephora });
		} else if (url.searchParams.get('retailer') === 'ms') {
			dispatch({ type: Actiontypes.UPDATE_RETAILER, value: Retailer.MS });
		} else if (url.searchParams.get('retailer')) {
			dispatch({ type: Actiontypes.UPDATE_RETAILER, value: Retailer.other });
		}

		if (url.searchParams.get('microsite') === 'true') {
			const directSkinDiag = url.searchParams.get('skindiag') === 'true';
			const directShadematch = url.searchParams.get('shadematch') === 'true';
			const directLanguage = url.searchParams.get('lang');
			const micrositeLocale = MICROSITE_MARKET_LANGUAGES.find((languageConfig) =>
				languageConfig.language === url.searchParams.get('lang')
				&& languageConfig.market === (url.searchParams.get('market') === 'GB' ? 'UK' : url.searchParams.get('market'))
			);

			initializeMicrositeGtm();
			dispatch({ type: Actiontypes.SET_MICROSITE, value: true });
			dispatch({ type: Actiontypes.SET_SHADEMATCH, value: directShadematch });
			dispatch({ type: Actiontypes.SET_LANGUAGE, value: directLanguage || i18n.language });
			dispatch({
				type: Actiontypes.SWITCH_STEP,
				value: directShadematch ? StepView.shadematchPrivacy : directSkinDiag ? StepView.onBoard : StepView.micrositeIntro,
			});

			if (micrositeLocale && (directSkinDiag || directShadematch)) {
				sendMicrositeCountrySelectedAnalytics(micrositeLocale.locale);
			}
		}

		let _market = url.searchParams.get('market');
		if (_market) {
			switch (_market) {
				case 'EMEA': // Europe -> Fr
					_market = 'FR';
					break;
				case 'EAEU':
					_market = 'RU';
					break;
				case 'GB': // Keep the UK application market; APIs handle their own aliases.
					_market = 'UK';
					break;
				case 'LATAM': // South America (latin) -> Brazil
					_market = 'BR';
					break;
				case 'ASIA':
					_market = 'CN';
					break;
				case 'PAC': // APAC -> Australia
					_market = 'AU';
					break;
				case 'ES':
				case 'SP':
					_market = 'ES';
					break;
				case 'NA':
					_market = 'US';
					break;
				default: //North America and Default
					break;
			}
			dispatch({ type: Actiontypes.SET_MARKET, value: _market });
		} else {
			dispatch({ type: Actiontypes.SET_MARKET, value: 'US' });
		}

		if (url.searchParams.get('parseJSON')) {
			parseLanguagesJSON();
		}

		return () => {
			window.removeEventListener('resize', updateViewportState);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		let parentOrigin = '';
		const url = new URL(window.location.href);
		const paramOrigin = url.searchParams.get('parentOrigin');
		if (paramOrigin) {
			parentOrigin = paramOrigin;
		} else if (document.referrer) {
			parentOrigin = new URL(document.referrer).origin;
		}

		if (parentOrigin && window.parentIFrame?.setTargetOrigin) {
			window.parentIFrame.setTargetOrigin(parentOrigin);
		}
		
	}, []);

	useEffect(() => {
		if (market) {
			dispatch({ type: Actiontypes.UPDATE_SESSION_ID, value: generateSessionID(market, i18n.language) });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [market, i18n.language]);

	useEffect(() => {
		if (!imageUser || !predictions) return;

		try {
			window.sessionStorage.setItem('debugLoadingMaskImage', imageUser);
			window.sessionStorage.setItem('debugLoadingMaskPredictions', JSON.stringify(predictions));
		} catch (error) {
			console.warn('[DebugLoadingMask]', 'Unable to cache latest capture for debug view', error);
		}
	}, [imageUser, predictions]);

	useEffect(() => {
		const iframeResizeHeight = (() => {
			switch (step) {
				case StepView.onBoard:
					return '600px';
				case StepView.tuto:
					return 'auto';
				default:
					return 'auto';
			}
		})();

		setIframeResizeTaggedElementHeight(iframeResizeHeight);

		console.log('[ClinicalReality iFrameResizer]', 'request size for step', {
			step,
			stepName: StepView[step],
			iframeResizeHeight,
			hasParentIFrame: Boolean(window.parentIFrame),
			iframeId: window.parentIFrame?.getId?.(),
		});
		window.parentIFrame?.size?.();
	}, [step]);

	const handleFooterDisplayed = () => {
		switch (step) {
			case StepView.results:
			case StepView.shadematchResults:
			case StepView.shadematchWristCamera:
			case StepView.quests:
				return false;
			default:
				return true;
		}
	};

	if (isDebugLoadingMask) {
		return (
			<AppStyled step={displayedStep}>
				<A3CameraContextProvider>
					<GlobalStyle />
					<DebugLoadingMask />
				</A3CameraContextProvider>
			</AppStyled>
		);
	}

	return (
		<AppStyled step={displayedStep}>
			<A3CameraContextProvider>
				<IframeResizeTaggedElement step={displayedStep}>
					<GlobalStyle />
					{isLandscape ? (
						<LandscapeComponent />
					) : (
						<>
							<Topper step={displayedStep} />
							<AnimatePresence>
								{displayedStep === StepView.micrositeIntro ? <MicrositeIntro /> : shouldRenderShadematch ? <ShadematchViewDispatcher /> : <ViewDispatcher />}
							</AnimatePresence>
							{handleFooterDisplayed() && <Footer step={displayedStep} />}
						</>
					)}
				</IframeResizeTaggedElement>
			</A3CameraContextProvider>
		</AppStyled>
	);
}

export default App;
